/**
 * useItemSync — the client inventory's item rows, edited on screen at once and
 * saved behind the thumb.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 * Every tap used to wait for its write AND a refetch of the whole payload, with
 * the page disabled in between. On a phone that is most of a second per tap:
 * "50 boxes" took minutes, and taps made while the page was locked were simply
 * dropped.
 *
 * Now:
 *   - a change is on screen the moment it is made;
 *   - writes go ~600ms after the last change to that row, so ten quick taps are
 *     one PATCH, not ten;
 *   - ONE WRITE PER ROW IN FLIGHT. A newer count waits for the older write and
 *     then goes, so two requests for one row can never land out of order;
 *   - the write's own answer updates the row (a POST's id) — there is no refetch;
 *   - a failed write leaves the count where the client put it and marks the row
 *     'error' with a retry. Nothing is quietly put back.
 *
 * The page refetches the whole payload only on load, on a path switch and after
 * submit, through replaceFromServer().
 *
 * ---------------------------------------------------------------------------
 * KEYS
 * ---------------------------------------------------------------------------
 * A row's key is its server id, or a temporary 't…' key until its POST lands —
 * and it KEEPS the temporary key after that, so the tile under a thumb does not
 * remount mid-tap. The shaped items hand the key out as `id`, which is all any
 * component uses an id for: a React key and a handle back into this hook.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import type { ClientItem } from './InventoryRequestView';

export type SyncStatus = 'idle' | 'pending' | 'saving' | 'error';

interface Row {
  key: string;
  /** Server id; null until the POST lands, and again after a DELETE. */
  id: string | null;
  catalogItemId: string | null;
  customName: string | null;
  name: string;
  roomKey: string | null;
  notes: string | null;
  icon: string | null;
  imageUrl: string | null;
  mine: boolean;
  /** What the client asked for. */
  qty: number;
  /** What the server last confirmed (0 when there is no row there). */
  saved: number;
  status: SyncStatus;
  error: string | null;
}

export interface NewItem {
  catalogItemId: string | null;
  customName: string | null;
  name: string;
  roomKey: string | null;
  icon: string | null;
  imageUrl: string | null;
  qty?: number;
}

type Call = (path: string, init?: RequestInit) => Promise<any>;

const DEBOUNCE_MS = 600;

const fromServer = (it: ClientItem): Row => ({
  key: it.id, id: it.id, catalogItemId: it.catalogItemId, customName: it.catalogItemId ? null : it.name,
  name: it.name, roomKey: it.roomKey, notes: it.notes, icon: it.icon, imageUrl: it.imageUrl, mine: it.mine,
  qty: it.quantity, saved: it.quantity, status: 'idle', error: null,
});

/** A rep's row cannot be stepped below 1 by the client — the server refuses it. */
const floorOf = (r: Row) => (r.mine ? 0 : 1);

const dirty = (r: Row) => (r.id ? r.qty !== r.saved : r.qty > 0);

export function useItemSync(call: Call, initial: ClientItem[]) {
  const rows = useRef<Map<string, Row>>(new Map(initial.map((it) => [it.id, fromServer(it)])));
  const timers = useRef<Record<string, number>>({});
  const inFlight = useRef<Map<string, Promise<void>>>(new Map());
  const seq = useRef(0);
  const [tick, setTick] = useState(0);
  const bump = useCallback(() => setTick((t) => t + 1), []);

  // flush is recursive (a newer count goes after the older write), so it is
  // reached through a ref rather than a closure over its own previous self.
  const flushRef = useRef<(key: string) => Promise<void>>(async () => {});

  flushRef.current = (key: string): Promise<void> => {
    window.clearTimeout(timers.current[key]);
    delete timers.current[key];

    // Already writing this row: the running write re-checks the count when it
    // lands and sends the newer one. Waiting on it is waiting for both.
    const running = inFlight.current.get(key);
    if (running) return running;

    const row = rows.current.get(key);
    if (!row) return Promise.resolve();
    if (!dirty(row)) {
      if (!row.id) rows.current.delete(key);          // added, then stepped back to 0 unsent
      else if (row.status !== 'idle') row.status = 'idle';
      bump();
      return Promise.resolve();
    }

    const want = row.qty;
    row.status = 'saving';
    row.error = null;
    bump();

    const write = async () => {
      try {
        if (!row.id) {
          const d = await call('/items', {
            method: 'POST',
            body: JSON.stringify(row.catalogItemId
              ? { catalog_item_id: row.catalogItemId, room_key: row.roomKey, quantity: want }
              : { custom_name: row.customName, room_key: row.roomKey, quantity: want }),
          });
          row.id = d.id;
          row.saved = want;
        } else if (want <= 0) {
          try {
            await call(`/items/${row.id}`, { method: 'DELETE' });
          } catch (e: any) {
            if (e?.status !== 404) throw e;                // already gone is what we wanted
          }
          row.id = null;
          row.saved = 0;
        } else {
          await call(`/items/${row.id}`, { method: 'PATCH', body: JSON.stringify({ quantity: want }) });
          row.saved = want;
        }
        row.status = 'idle';
      } catch (e: any) {
        row.status = 'error';
        row.error = e?.message || 'Not saved';
      }
    };

    const p = write().then(async () => {
      inFlight.current.delete(key);
      if (row.status === 'error' || rows.current.get(key) !== row) { bump(); return; }
      if (dirty(row)) { await flushRef.current(key); return; }
      if (!row.id) rows.current.delete(key);
      bump();
    });
    inFlight.current.set(key, p);
    return p;
  };

  const schedule = useCallback((key: string) => {
    window.clearTimeout(timers.current[key]);
    timers.current[key] = window.setTimeout(() => { void flushRef.current(key); }, DEBOUNCE_MS);
  }, []);

  const setQty = useCallback((key: string, n: number) => {
    const row = rows.current.get(key);
    if (!row) return;
    row.qty = Math.max(floorOf(row), Math.min(999, Math.trunc(n) || 0));
    if (!inFlight.current.has(key)) row.status = dirty(row) ? 'pending' : (row.status === 'error' ? 'error' : 'idle');
    bump();
    schedule(key);
  }, [bump, schedule]);

  const add = useCallback((it: NewItem) => {
    const key = `t${++seq.current}`;
    rows.current.set(key, {
      key, id: null, catalogItemId: it.catalogItemId, customName: it.customName, name: it.name,
      roomKey: it.roomKey, notes: null, icon: it.icon, imageUrl: it.imageUrl, mine: true,
      qty: Math.max(1, Math.min(999, it.qty ?? 1)), saved: 0, status: 'pending', error: null,
    });
    bump();
    schedule(key);
    return key;
  }, [bump, schedule]);

  const retry = useCallback((key: string) => { void flushRef.current(key); }, []);

  /** Send everything still waiting. Resolves true when every row is saved. */
  const flushAll = useCallback(async () => {
    const keys = [...rows.current.keys()].filter((k) =>
      dirty(rows.current.get(k)!) || timers.current[k] !== undefined || inFlight.current.has(k));
    await Promise.all(keys.map((k) => flushRef.current(k)));
    return ![...rows.current.values()].some((r) => r.status === 'error');
  }, []);

  /**
   * A fresh payload from the server. Rows still waiting, writing or failed are
   * KEPT AS THEY ARE — the server has not heard about them yet, and replacing
   * them would throw the client's count away.
   */
  const replaceFromServer = useCallback((items: ClientItem[]) => {
    const keep = [...rows.current.values()].filter((r) =>
      r.status !== 'idle' || dirty(r) || inFlight.current.has(r.key) || timers.current[r.key] !== undefined);
    const keptIds = new Set(keep.map((r) => r.id).filter(Boolean) as string[]);
    const next = new Map<string, Row>();
    for (const it of items) if (!keptIds.has(it.id)) next.set(it.id, fromServer(it));
    for (const r of keep) next.set(r.key, r);
    rows.current = next;
    bump();
  }, [bump]);

  /**
   * The rows as the page draws them. A row stepped to 0 is gone from the list at
   * once — unless its delete failed, in which case it stays, at 0, with Retry.
   */
  const items = useMemo((): (ClientItem & { status: SyncStatus; error: string | null })[] => {
    void tick;
    return [...rows.current.values()]
      .filter((r) => r.qty > 0 || r.status === 'error')
      .map((r) => ({
        id: r.key, catalogItemId: r.catalogItemId, name: r.name, quantity: r.qty, roomKey: r.roomKey,
        notes: r.notes, icon: r.icon, imageUrl: r.imageUrl, mine: r.mine, status: r.status, error: r.error,
      }));
  }, [tick]);

  const saving = items.some((i) => i.status === 'pending' || i.status === 'saving')
    || [...rows.current.values()].some((r) => r.status === 'pending' || r.status === 'saving');
  const failed = [...rows.current.values()].filter((r) => r.status === 'error').length;

  return { items, setQty, add, retry, flushAll, replaceFromServer, saving, failed };
}
